import TimeframeHeader from 'Components/Admin/Analytics/TimeframeHeader';
import { getAnalyticsForProfessionalApi } from 'Constants';
import { Context } from 'Context/Context';
import React, { useContext, useEffect, useState } from 'react'
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import moment from 'moment';
import CountUp from 'react-countup';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

ChartJS.register(ArcElement, Tooltip, Legend);

// One KPI tile - big Spectral value with a small trend pill showing the
// change vs. the immediately preceding period of the same length. The pill
// is omitted (rather than faked) when there's no prior-period baseline to
// compare against (e.g. previous count was 0).
function KpiTile({ label, value, delta }) {
  const hasDelta = delta !== null && delta !== undefined && isFinite(delta);
  const isPositive = hasDelta && delta > 0;
  const isNegative = hasDelta && delta < 0;
  return (
    <div className='rounded-md border border-gray-200 bg-white px-4 py-4'>
      <div className='text-xs text-gray-500 font-plexsans mb-2'>{label}</div>
      <div className='flex items-end justify-between gap-2 flex-wrap'>
        <div className='font-spectral text-navy text-2xl lg:text-[26px] leading-none'>
          <CountUp end={value || 0} duration={1.2} separator="," />
        </div>
        {hasDelta && (isPositive || isNegative) && (
          <div className={'flex items-center gap-0.5 text-[11px] font-semibold font-plexsans px-1.5 py-0.5 rounded ' + (isPositive ? 'text-[#3a7d4f] bg-[#3a7d4f]/10' : 'text-[#9a3b2e] bg-[#9a3b2e]/10')}>
            {isPositive ? <ArrowUpRight className='w-3 h-3' /> : <ArrowDownRight className='w-3 h-3' />}
            {Math.abs(delta)}%
          </div>
        )}
      </div>
    </div>
  );
}

// A compact "your share of X" donut - center label is the percentage,
// legend on the side spells out the raw counts behind it.
function SharePanel({ title, userCount, totalCount, color }) {
  const pct = totalCount ? Math.round((userCount / totalCount) * 100) : 0;
  const data = {
    labels: [title, 'Rest of platform'],
    datasets: [
      {
        data: [userCount || 0, Math.max((totalCount || 0) - (userCount || 0), 0)],
        backgroundColor: [color, 'rgba(20,33,61,0.08)'],
        hoverBackgroundColor: [color, 'rgba(20,33,61,0.12)'],
        borderWidth: 0,
      },
    ],
  };
  const options = {
    cutout: '72%',
    plugins: { legend: { display: false } },
  };
  return (
    <div className='rounded-md border border-gray-200 bg-white p-5 flex-1 min-w-0'>
      <div className='font-plexsans text-sm font-semibold text-navy mb-5'>{title}</div>
      <div className='flex items-center gap-6 flex-wrap'>
        <div className='relative w-[110px] h-[110px] flex-none'>
          <Doughnut data={data} options={options} />
          <div className='absolute inset-0 flex items-center justify-center'>
            <div className='font-spectral text-navy text-xl'>{pct}%</div>
          </div>
        </div>
        <div className='font-plexsans'>
          <div className='flex items-center gap-2 text-xs text-navy mb-2'>
            <span className='w-2 h-2 rounded-full flex-none' style={{ background: color }}></span>
            Yours: {userCount || 0}
          </div>
          <div className='flex items-center gap-2 text-xs text-gray-500 mb-2'>
            <span className='w-2 h-2 rounded-full flex-none bg-navy/10'></span>
            Platform: {totalCount || 0}
          </div>
          <div className='text-[11px] text-gray-400'>{userCount || 0} of {totalCount || 0}</div>
        </div>
      </div>
    </div>
  );
}

const COMPARISON_LABEL = {
  today: 'Compared to yesterday.',
  week: 'Compared to the previous 7 days.',
  month: 'Compared to the previous 30 days.',
  custom: 'Compared to the previous period.',
};

function ProfessionalStatisticsGraph() {
  const { userDetails, makeApiCallFunc } = useContext(Context);
  const [caseData, setCaseData] = useState(null);
  const [prevCaseData, setPrevCaseData] = useState(null);
  const [frame, setFrame] = useState("week");
  const [loadCaseAnalytics, setLoadCaseAnalytics] = useState(true);

  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      endDate: new Date(Date.now()),
      key: 'selection'
    }
  ]);

  useEffect(() => {
    // Current period
    makeApiCallFunc(getAnalyticsForProfessionalApi,
      {
        userId: userDetails._id,
        startTime: dateRange[0]?.startDate,
        endTime: dateRange[0]?.endDate
      },
      ({ data }) => {
        setCaseData(data);
      },
      (error) => {
        console.log(error);
      }
    )

    // Immediately preceding period of the same length, purely for the
    // trend deltas on the KPI tiles above - no new backend endpoint,
    // just a second call to the same existing one with shifted dates.
    const start = new Date(dateRange[0]?.startDate);
    const end = new Date(dateRange[0]?.endDate);
    const durationMs = Math.max(end - start, 24 * 60 * 60 * 1000);
    const prevEnd = new Date(start.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - durationMs);

    makeApiCallFunc(getAnalyticsForProfessionalApi,
      {
        userId: userDetails._id,
        startTime: prevStart,
        endTime: prevEnd
      },
      ({ data }) => {
        setPrevCaseData(data);
      },
      (error) => {
        console.log(error);
      }
    )
  }, [loadCaseAnalytics])

  const isSingleDay = () => {
    const date1 = moment(dateRange[0]?.startDate);
    const date2 = moment(dateRange[0]?.endDate);
    return date1.isSame(date2, "date");
  }

  const pctDelta = (curr, prev) => {
    if (!prevCaseData || prev === undefined || prev === null || prev === 0) return null;
    if (curr === undefined || curr === null) return null;
    return Math.round(((curr - prev) / prev) * 100);
  };

  const kpiCards = caseData ? [
    { label: 'Your requests sent', value: caseData?.totalUserRequests, delta: pctDelta(caseData?.totalUserRequests, prevCaseData?.totalUserRequests) },
    { label: 'Your closed cases', value: caseData?.totalUserClosed, delta: pctDelta(caseData?.totalUserClosed, prevCaseData?.totalUserClosed) },
    { label: 'Platform requests', value: caseData?.totalRequests, delta: pctDelta(caseData?.totalRequests, prevCaseData?.totalRequests) },
    { label: 'Total cases posted', value: caseData?.totalCases, delta: pctDelta(caseData?.totalCases, prevCaseData?.totalCases) },
  ] : [];

  return (
    <div className='w-full bg-ivory border border-gray-200 shadow-sm rounded-xl p-4 lg:p-8'>
      <div className='flex flex-wrap items-start justify-between gap-3 mb-5'>
        <div>
          <div className='font-spectral text-navy text-xl lg:text-2xl'>Your activity</div>
          <div className='font-plexsans mt-1' style={{ fontSize: '13px', color: 'rgba(20,33,61,.5)' }}>
            {COMPARISON_LABEL[frame] || COMPARISON_LABEL.custom}
            {dateRange[0]?.startDate && dateRange[0]?.endDate &&
              <span style={{ color: 'rgba(20,33,61,.4)' }}>
                &nbsp;&middot;&nbsp;
                {isSingleDay()
                  ? moment(dateRange[0].startDate).format('MMM D, YYYY')
                  : moment(dateRange[0].startDate).format('MMM D') + ' – ' + moment(dateRange[0].endDate).format('MMM D, YYYY')}
              </span>
            }
          </div>
        </div>
        <TimeframeHeader className='flex items-center' frame={frame} setFrame={setFrame} dateRange={dateRange} setDateRange={setDateRange} setLoadCaseAnalytics={setLoadCaseAnalytics} />
      </div>

      <div className='w-full'>
        {caseData &&
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 pb-6 lg:pb-8 w-full'>
            {kpiCards.map((kpi) => (
              <KpiTile key={kpi.label} label={kpi.label} value={kpi.value} delta={kpi.delta} />
            ))}
          </div>
        }

        <div className='flex w-full lg:flex-row flex-col gap-3'>
          {caseData && caseData?.totalRequests !== 0 &&
            <SharePanel
              title="Your share of platform requests"
              userCount={caseData?.totalUserRequests}
              totalCount={caseData?.totalRequests}
              color="#8a6231"
            />
          }
          {caseData && caseData?.totalClosed !== 0 &&
            <SharePanel
              title="Your share of closed cases"
              userCount={caseData?.totalUserClosed}
              totalCount={caseData?.totalClosed}
              color="#c9a26a"
            />
          }
        </div>
      </div>
    </div>
  )
}

export default ProfessionalStatisticsGraph
